/* SISU Inspiration gallery (assets/sisu-inspiration.js): filters, show more and the setup viewer.
   Vanilla JS, no libraries, loaded with defer. It only enhances server-rendered markup: with JS off
   every setup and every photo link still works. All data is read from the DOM.
   History and analytics: filters are read from ?setting/?type/?model/?addon on load (inbound links,
   shared links) but never written back, because GA4 counts every query-string rewrite as a page view
   and the Meta pixel counts every URL change. The only history entry is one pushState (#setup-<id>)
   when a visitor opens a setup, so the phone's Back button closes the viewer. */
(() => {
  // html.sisu-insp-nojs gives the static gallery (the CSS treats it like .no-js). It is also set by the
  // script tag's onerror when this file fails to load.
  const staticGallery = () => document.documentElement.classList.add('sisu-insp-nojs');
  if (!window.customElements) return staticGallery();
  if (customElements.get('sisu-inspiration')) return;
  const FACETS = ['setting', 'type', 'model', 'addon'];
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const words = (s) => (s || '').split(' ').filter(Boolean);
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => `&#${c.charCodeAt(0)};`);
  const smooth = () => (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');
  const setups = (k) => `${k} setup${k === 1 ? '' : 's'}`;
  const modified = (e) => e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button > 0;
  const hashId = () => {
    const m = /^#setup-(.+)$/.exec(location.hash);
    try { return m ? decodeURIComponent(m[1]) : null; } catch { return null; }
  };
  const MQ = ['(min-width: 1100px)', '(min-width: 750px)'].map((q) => matchMedia(q));
  const columns = () => (MQ[0].matches ? 4 : MQ[1].matches ? 3 : 2);
  // Viewer image slot: full width on phones (stage height capped at 64vh); on desktop the stage is the
  // dialog minus the 400px info column, and minus the 6rem setup bar in height.
  const viewSizes = (a) => `(min-width: 990px) min(calc(min(1280px, 100vw - 48px) - 400px), calc((min(860px, 100vh - 48px) - 60px) * ${a})), min(100vw, calc(64vh * ${a}))`;
  // Every hook the script touches. If the Liquid output misses one, the gallery stays static (all
  // setups visible, photo links working, filter UI hidden) instead of failing halfway. The optional
  // live regions ([data-sheet-status], [data-lb-live]) are left out on purpose.
  const HOOKS = ['[data-grid]', '[data-lb]', '[data-sheet]', '[data-track]', '[data-bar]', '[data-status]', '[data-count]', '[data-pills]',
    '.sisu-insp-status [data-clear]', '[data-active]', '[data-sheet-open]', '[data-match]', '[data-empty]', '[data-more]', '[data-more-text]',
    '[data-close]', '[data-counter]', '[data-lb-model]', '[data-lb-price]', '[data-lb-title]', '[data-lb-owner]', '[data-lb-quote]', '[data-lb-shop]',
    '[data-lb-shop-label]', '[data-lb-compare]', '[data-lb-spec]', '[data-lb-more]', '[data-lb-shared]', '[data-lb-pos]',
    '[data-setup="-1"] [data-step-title]', '[data-setup="1"] [data-step-title]'];
  const X = '<svg class="sisu-insp-icon" aria-hidden="true" focusable="false" viewBox="0 0 20 20" width="14" height="14"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.6" fill="none"/></svg>';

  class SisuInspiration extends HTMLElement {
    connectedCallback() {
      if (!this.cards) {
        let ok = false;
        try { ok = this.init(); } catch (e) { console.error('sisu-inspiration: gallery left static', e); }
        if (!ok) return this.fallback();
      }
      this.bind(true);
      if (!this.started) {
        this.started = true;
        // Deep link or reload. If the entry is one we pushed (a reload, or Back from another page without the
        // back-forward cache), closing goes back to the grid entry instead of leaving a duplicate one.
        const id = hashId();
        if (id && this.open(id, false)) this.pushed = !!(history.state && history.state.sisuInsp);
      }
    }

    disconnectedCallback() {
      if (this.cards) this.bind(false);
    }

    init() {
      const q = (s) => $(s, this);
      const missing = HOOKS.filter((s) => !q(s));
      if (missing.length) {
        console.warn(`sisu-inspiration: markup hooks missing (${missing.join(', ')}); gallery left static`);
        return false;
      }
      [this.grid, this.lb, this.sheet, this.track, this.bar, this.status] = HOOKS.slice(0, 6).map(q);
      this.q = q;
      this.rows = +this.dataset.rows || 6;
      this.cols = columns();
      this.page = this.limit = this.cols * this.rows;
      this.cards = $$('.sisu-insp-card', this).map((el) => ({
        el,
        id: el.dataset.id,
        link: $('[data-open]', el),
        f: Object.fromEntries(FACETS.map((f) => [f, words(el.dataset[f])])),
      }));
      this.ctrls = $$('[data-facet]', this);
      this.labels = {};
      this.ctrls.forEach((x) => (this.labels[`${x.dataset.facet}:${x.dataset.value}`] = x.dataset.label));
      this.header = this.dataset.header ? $(this.dataset.header) : null;

      this.state = this.read();
      this.apply();
      this.grid.dataset.ready = '';
      this.classList.remove('is-pending');

      this.addEventListener('click', this);
      this.addEventListener('change', this);
      [this.lb, this.sheet].forEach((d) => d.addEventListener('click', (e) => e.target === d && d.close()));
      this.lb.addEventListener('close', () => this.closed());
      this.lb.addEventListener('keydown', (e) => {
        const k = { ArrowLeft: -1, ArrowRight: 1 }[e.key];
        if (k && !e.altKey && !e.target.matches('input, textarea')) e.preventDefault(), this.nav(k);
      });
      // Swipes on the photo. Between a setup's own photos the strip's native scroll-snap does the work. Past its first
      // or last photo (most setups have one) a horizontal swipe moves to the previous or next setup. If the strip did
      // not move under a swipe that should have moved it, the swipe steps the photo itself. Passive listeners only, so
      // vertical scrolling of the viewer is untouched.
      const stage = this.track.parentElement;
      stage.addEventListener('touchstart', (e) => {
        const t = e.touches[0];
        this.sw = e.touches.length === 1 ? { x: t.clientX, y: t.clientY, s: this.track.scrollLeft, i: this.pi } : null;
      }, { passive: true });
      stage.addEventListener('touchend', (e) => {
        const s = this.sw;
        this.sw = null;
        if (!s || e.touches.length) return;
        const t = e.changedTouches[0], dx = t.clientX - s.x, dy = t.clientY - s.y;
        if (Math.abs(dx) < 50 || Math.abs(dx) < 1.5 * Math.abs(dy)) return;
        if (Math.abs(this.track.scrollLeft - s.s) > (this.track.clientWidth || 1) * 0.15) return;
        this.nav(dx < 0 ? 1 : -1, s.i);
      }, { passive: true });
      stage.addEventListener('touchcancel', () => (this.sw = null), { passive: true });
      stage.addEventListener('animationend', () => delete stage.dataset.enter);
      this.sheet.addEventListener('close', () => {
        this.lock(false);
        const sl = q('[data-sheet-status]');
        if (sl) sl.textContent = '';
        if (this.url() !== this.sheetUrl) this.toGrid();
        else q('[data-sheet-open]').focus();
      });
      this.track.addEventListener('scroll', () => {
        clearTimeout(this.st);
        this.st = setTimeout(() => this.photo(), 80);
      }, { passive: true });

      // Window and document listeners are added in bind() and removed on disconnect, so a theme
      // editor re-render (shopify:section:load) leaves no stale instance behind.
      this.onPop = (e) => this.pop(e);
      this.onSet = (e) => this.setLink(e);
      this.onCols = () => this.relayout();
      this.onScroll = () => {
        this.until = performance.now() + 300;
        if (!this.raf) this.raf = requestAnimationFrame(this.tick);
      };
      // Keep the sticky bar just under the theme header while the header shows (it hides on scroll
      // down and slides back on scroll up). The offset is a CSS variable used in a transform.
      this.tick = () => {
        const hb = this.header ? Math.max(0, this.header.getBoundingClientRect().bottom) : 0;
        const b = this.bar.getBoundingClientRect(), g = this.getBoundingClientRect();
        const top = b.top - (this.off || 0);
        const off = Math.round(Math.max(0, Math.min(hb - top, g.bottom - b.height - top)));
        if (off !== this.off) (this.off = off), this.style.setProperty('--sisu-insp-off', `${off}px`);
        this.raf = performance.now() < this.until ? requestAnimationFrame(this.tick) : 0;
      };
      return true;
    }

    // Static gallery, as with JS off: every setup and photo link shown, filter bar, status line and
    // Show more hidden (CSS keys on html.sisu-insp-nojs).
    fallback() {
      this.cards = null;
      staticGallery();
      this.classList.remove('is-pending');
      $$('.sisu-insp-card[hidden]', this).forEach((c) => (c.hidden = false));
    }

    bind(on) {
      const m = on ? 'addEventListener' : 'removeEventListener';
      window[m]('popstate', this.onPop);
      window[m]('scroll', this.onScroll, { passive: true });
      window[m]('resize', this.onScroll);
      document[m]('click', this.onSet);
      MQ.forEach((x) => x[m]('change', this.onCols));
      if (!on) cancelAnimationFrame(this.raf), (this.raf = 0);
    }

    handleEvent(e) {
      const t = e.target;
      if (e.type === 'change') return t.dataset.facet && this.toggle(t.dataset.facet, t.dataset.value, t.checked);
      const b = t.closest('button, a');
      if (!b || !this.contains(b)) return;
      const d = b.dataset;
      if ('open' in d) {
        if (modified(e)) return; // new tab, new window and download still work
        e.preventDefault();
        return this.open(b.closest('.sisu-insp-card').dataset.id, true);
      }
      if (d.facet && b.tagName === 'BUTTON') this.toggle(d.facet, d.value, b.getAttribute('aria-pressed') !== 'true');
      else if (d.remove) {
        const i = $$('[data-remove]', this).indexOf(b);
        this.toggle(...d.remove.split(':'), false);
        const pills = $$('[data-remove]', this);
        (pills[Math.min(i, pills.length - 1)] || this.status).focus({ preventScroll: true });
      } else if ('clear' in d) {
        this.set({});
        if (!this.sheet.open) this.status.focus({ preventScroll: true });
      } else if ('moreBtn' in d) this.more();
      else if ('sheetOpen' in d) (this.sheetUrl = this.url()), this.sheet.showModal(), this.lock(true);
      else if ('close' in d) this.lb.close();
      else if (d.photo) this.go(this.pi + +d.photo);
      else if (d.setup) this.step(+d.setup, b);
      else if ('lbMore' in d) this.closeThen(() => (this.set(this.moreSet), this.toGrid()));
      else if ('lbCompare' in d) this.closeThen(() => this.toFit());
      else if ('lbShare' in d) this.share();
    }

    // ---------- filters ----------
    // Filters come from the URL once, on load. After that they live in memory only (see the note at the top).
    read() {
      const p = new URLSearchParams(location.search), s = {};
      FACETS.forEach((f) => (s[f] = new Set((p.get(f) || '').split(',').filter((v) => this.labels[`${f}:${v}`]))));
      return s;
    }
    // The page URL with the current filters: used to build the Share link (and to tell whether the Filters sheet
    // changed anything). Never written to the address bar.
    url(hash = location.hash) {
      const p = new URLSearchParams(location.search);
      FACETS.forEach((f) => {
        const v = [...this.state[f]].join(',');
        v ? p.set(f, v) : p.delete(f);
      });
      const s = p.toString().replace(/%2C/gi, ',');
      return location.pathname + (s ? `?${s}` : '') + hash;
    }
    match(c, skip) {
      return FACETS.every((f) => f === skip || !this.state[f].size || c.f[f].some((v) => this.state[f].has(v)));
    }
    toggle(f, v, on) {
      this.state[f][on ? 'add' : 'delete'](v);
      this.changed();
    }
    set(o = {}) {
      FACETS.forEach((f) => (this.state[f] = new Set((o[f] || []).filter((v) => this.labels[`${f}:${v}`]))));
      this.changed();
    }
    changed() {
      this.limit = this.page;
      this.apply();
      // Never leave someone looking at an empty stretch of page: bring the results back into view.
      if (!this.sheet.open && this.status.getBoundingClientRect().top < 0) this.status.scrollIntoView();
    }
    apply() {
      const s = this.state, q = this.q;
      this.list = this.cards.filter((c) => this.match(c));
      const total = this.list.length, shown = new Set(this.list.slice(0, this.limit));
      this.cards.forEach((c) => (c.el.hidden = !shown.has(c)));
      this.ctrls.forEach((x) => {
        const { facet: f, value: v } = x.dataset, on = s[f].has(v);
        const k = this.cards.filter((c) => c.f[f].includes(v) && this.match(c, f)).length;
        const n = $('[data-n]', x.closest('label') || x);
        if (n) n.textContent = k;
        // A control with no matches is disabled, except the one that has focus (the filter just turned
        // off): disabling it would drop keyboard focus to <body>. It is disabled on the next apply.
        x.disabled = !k && !on && x !== document.activeElement;
        x.type === 'checkbox' ? (x.checked = on) : x.setAttribute('aria-pressed', on);
      });
      const active = FACETS.flatMap((f) => [...s[f]].map((v) => `${f}:${v}`));
      const pills = q('[data-pills]');
      pills.innerHTML = active.map((k) => {
        const l = esc(this.labels[k]);
        return `<li><button type="button" class="sisu-insp-pill" data-remove="${k}" aria-label="Remove filter: ${l}">${l}${X}</button></li>`;
      }).join('');
      pills.hidden = q('.sisu-insp-status [data-clear]').hidden = !active.length;
      const badge = q('[data-active]'), open = q('[data-sheet-open]');
      badge.textContent = active.length;
      badge.hidden = !active.length;
      open.setAttribute('aria-label', active.length ? `Filters, ${active.length} selected` : 'Filters');
      const msg = `Showing ${setups(total)}`, count = q('[data-count]');
      if (count.textContent !== msg) count.textContent = msg;
      // The modal Filters sheet hides the page's count from screen readers, so it has its own live region.
      const sl = q('[data-sheet-status]'), sm = this.sheet.open ? msg : '';
      if (sl && sl.textContent !== sm) sl.textContent = sm;
      q('[data-match]').textContent = total ? `Show ${setups(total)}` : 'No setups';
      q('[data-empty]').hidden = total > 0;
      q('[data-more]').hidden = total <= this.limit;
      q('[data-more-text]').textContent = `Showing ${Math.min(this.limit, total)} of ${total}`;
    }
    more() {
      const first = this.list[this.limit];
      this.limit += this.page;
      this.apply();
      if (first && first.link) first.link.focus();
    }
    relayout() {
      const cols = columns();
      if (cols === this.cols) return;
      const pages = Math.max(1, Math.round(this.limit / this.page));
      this.cols = cols;
      this.page = cols * this.rows;
      this.limit = pages * this.page;
      this.apply();
    }
    toGrid() {
      this.status.scrollIntoView({ behavior: smooth() });
      this.status.focus({ preventScroll: true });
    }
    toFit() {
      const f = $('#sisu-insp-fit'), h = f && $('h2', f);
      if (!f) return this.toGrid();
      f.scrollIntoView({ behavior: smooth() });
      if (h) h.setAttribute('tabindex', '-1'), h.focus({ preventScroll: true });
    }
    setLink(e) {
      const a = e.target.closest && e.target.closest('[data-sisu-insp-set]');
      if (!a || modified(e)) return;
      e.preventDefault();
      const p = new URLSearchParams(a.dataset.sisuInspSet);
      this.set(Object.fromEntries(FACETS.map((f) => [f, (p.get(f) || '').split(',')])));
      this.toGrid();
    }
    lock(on) {
      document.body.classList.toggle('overflow-hidden', on);
    }

    // ---------- setup viewer ----------
    open(id, push) {
      const c = this.cards.find((x) => x.id === id);
      if (!c || !this.fill(c)) return false;
      if (!this.lb.open) {
        try { this.lb.showModal(); } catch { return false; }
        this.lock(true);
      }
      // One history entry when a visitor opens a setup, so the phone's Back button closes the viewer. Only the hash
      // changes. Previous/Next setup and the photo arrows write no history; this.cur holds the setup on screen.
      if (push) history.pushState({ sisuInsp: 1 }, '', `${location.pathname}${location.search}#setup-${encodeURIComponent(id)}`), (this.pushed = true);
      return true;
    }
    fill(c) {
      const li = c.el, d = li.dataset, a = c.link, img = a && $('img', a);
      const text = (s) => ($(s, li)?.textContent || '').trim();
      const v = (s) => $(s, this.lb);
      if (!a || !img) return false;
      this.cur = c;
      const model = text('.sisu-insp-card__model');
      this.photos = [
        { src: a.href, srcset: a.dataset.srcset, w: +a.dataset.w, h: +a.dataset.h, alt: img.alt },
        ...$$('.sisu-insp-card__photos a', li).map((x) => ({ src: x.href, srcset: x.dataset.srcset, w: +x.dataset.w, h: +x.dataset.h, alt: x.dataset.alt || '' })),
      ].filter((p) => p.w && p.h);
      this.track.replaceChildren(...this.photos.map((p) => {
        const i = document.createElement('img'), item = document.createElement('li');
        Object.assign(i, { width: p.w, height: p.h, alt: p.alt, decoding: 'async' });
        item.append(i);
        return item;
      }));
      const tallest = Math.max(...this.photos.map((p) => p.h / p.w));
      this.lb.style.setProperty('--sisu-insp-ar', Math.min(1.25, tallest).toFixed(4));

      v('[data-lb-model]').textContent = model;
      const price = v('[data-lb-price]');
      price.textContent = d.price || '';
      price.hidden = !d.price;
      v('[data-lb-title]').textContent = text('.sisu-insp-card__title');
      const owner = text('.sisu-insp-card__owner'), ownerEl = v('[data-lb-owner]');
      ownerEl.textContent = owner;
      ownerEl.hidden = !owner;
      const quote = $('.sisu-insp-card__quote', li), quoteEl = v('[data-lb-quote]');
      quoteEl.replaceChildren(...(quote ? [...quote.childNodes].map((n) => n.cloneNode(true)) : []));
      quoteEl.hidden = !quote;
      const shop = v('[data-lb-shop]');
      shop.hidden = !d.url;
      v('[data-lb-compare]').hidden = !!d.url;
      if (d.url) (shop.href = d.url), (v('[data-lb-shop-label]').textContent = `Shop the ${d.shop || model}`);
      const rows = [['Model', model], ['Setting', text('.sisu-insp-card__setting').replace(/ · /g, ', ')], ['Add-ons', d.addons || '']];
      v('[data-lb-spec]').replaceChildren(...rows.filter((r) => r[1]).flatMap(([k, val]) => {
        const dt = document.createElement('dt'), dd = document.createElement('dd');
        dt.textContent = k;
        dd.textContent = val;
        return [dt, dd];
      }));
      // "See more" stays inside the browse: same model when it is confirmed, otherwise same sauna type.
      const more = v('[data-lb-more]');
      const facet = d.model ? 'model' : 'type', value = d.model || d.type;
      const same = value ? this.cards.filter((x) => x.f[facet].includes(value)).length : 0;
      this.moreSet = { [facet]: [value] };
      more.hidden = same < 2;
      more.textContent = d.model ? `See more ${model} setups (${same})` : `See all ${value} setups (${same})`;
      v('[data-lb-shared]').textContent = '';

      const list = this.list.includes(c) ? this.list : this.cards;
      const i = list.indexOf(c), L = list.length;
      v('[data-lb-pos]').textContent = `Setup ${i + 1} of ${L}`;
      [[-1, 'Previous'], [1, 'Next']].forEach(([dir, word]) => {
        const b = v(`[data-setup="${dir}"]`), n = list[i + dir];
        const title = n ? ($('.sisu-insp-card__title', n.el)?.textContent || '').trim() : '';
        b.disabled = !n;
        b.setAttribute('aria-label', n ? `${word} setup: ${title}` : `${word} setup`);
        $('[data-step-title]', b).textContent = title;
      });
      const f = document.activeElement;
      if (!this.lb.contains(f) || f.disabled) v('[data-close]').focus();
      this.track.scrollLeft = 0;
      this.photo(0);
      return true;
    }
    photo(i = Math.round(this.track.scrollLeft / (this.track.clientWidth || 1))) {
      const P = this.photos || [], n = P.length, imgs = $$('img', this.track);
      if (!n) return;
      this.pi = i = Math.max(0, Math.min(n - 1, i));
      for (let k = i - 1; k <= i + 1; k++) {
        const img = imgs[k], p = P[k];
        if (!img || img.getAttribute('src')) continue;
        img.sizes = viewSizes((p.w / p.h).toFixed(3));
        if (p.srcset) img.srcset = p.srcset;
        img.src = p.src;
      }
      const counter = $('[data-counter]', this.lb);
      counter.innerHTML = n > 1 ? `<span aria-hidden="true">${i + 1} / ${n}</span><span class="visually-hidden">Photo ${i + 1} of ${n}</span>` : '';
      $$('[data-photo]', this.lb).forEach((b) => {
        b.hidden = n < 2;
        const off = b.dataset.photo < 0 ? i === 0 : i === n - 1;
        if (off && document.activeElement === b) this.track.focus({ preventScroll: true });
        b.disabled = off;
      });
    }
    go(i) {
      if (!this.photos || i < 0 || i >= this.photos.length) return;
      this.track.scrollTo({ left: i * this.track.clientWidth, behavior: smooth() });
      this.photo(i);
    }
    // Arrow keys and swipes: this setup's next or previous photo if there is one, else the next or previous setup,
    // which slides in from the side it came from.
    nav(dir, from = this.pi) {
      const j = from + dir;
      if (this.photos && j >= 0 && j < this.photos.length) return this.go(j);
      const L = this.list.includes(this.cur) ? this.list : this.cards;
      if (!L[L.indexOf(this.cur) + dir]) return;
      const stage = this.track.parentElement;
      delete stage.dataset.enter;
      void stage.offsetWidth;
      stage.dataset.enter = dir > 0 ? 'next' : 'prev';
      this.step(dir);
    }
    step(dir) {
      const L = this.list.includes(this.cur) ? this.list : this.cards, c = L[L.indexOf(this.cur) + dir];
      // Focus stays on Previous/Next, so announce the setup now showing ("Setup 6 of 49: title").
      if (c && this.open(c.id, false)) this.say(`${$('[data-lb-pos]', this.lb).textContent}: ${$('[data-lb-title]', this.lb).textContent}`);
    }
    say(t) {
      const live = $('[data-lb-live]', this.lb);
      if (live) live.textContent = t;
    }
    async share() {
      // Built from the setup on screen (the address bar keeps the one first opened) and the current filters.
      const out = $('[data-lb-shared]', this.lb), url = location.origin + this.url(`#setup-${encodeURIComponent(this.cur.id)}`);
      if (navigator.share) {
        try { return await navigator.share({ title: document.title, url }); } catch (e) { if (e && e.name === 'AbortError') return; }
      }
      try {
        await navigator.clipboard.writeText(url);
        out.textContent = 'Link copied.';
      } catch {
        out.textContent = `Copy this link: ${url}`;
      }
    }
    closeThen(fn) {
      this.afterClose = fn;
      this.lb.close();
    }
    closed() {
      this.lock(false);
      this.say('');
      if (this.pushed) {
        // Drop the #setup entry we added. The popstate it fires (always, for a same-document entry)
        // finishes the close in pop(); no timer involved.
        this.pushed = false;
        this.closing = true;
        history.back();
      } else {
        // A fresh deep link (no entry of ours): drop the #setup hash in place. The query string is left as it is.
        // (After the visitor's own Back press the hash is already gone, and the page keeps its scroll.)
        const deep = !!hashId();
        if (deep) history.replaceState(history.state, '', location.pathname + location.search);
        this.settle(deep);
      }
    }
    settle(reveal = false) {
      const fn = this.afterClose, c = this.cur;
      this.afterClose = null;
      // After a history traversal the browser restores the entry's scroll position once popstate
      // has run, so a follow-up scroll (Compare the models, See more) waits one frame.
      if (fn) return requestAnimationFrame(fn);
      if (!c) return;
      const i = this.list.indexOf(c);
      if (i >= this.limit) (this.limit = Math.ceil((i + 1) / this.page) * this.page), this.apply();
      // preventScroll: focusing a card taller than the screen (phone landscape) scrolled the page on close. The page
      // stays where the visitor left it. Only after a deep link, which opened on top of the page's first screen, is
      // the card brought into view when it is entirely off screen.
      const el = c.el.hidden ? this.status : c.link;
      el.focus({ preventScroll: true });
      if (reveal) {
        const b = el.getBoundingClientRect();
        if (b.bottom <= 0 || b.top >= innerHeight) el.scrollIntoView({ block: 'center' });
      }
    }
    // Back/Forward only open or close the viewer from the #setup hash. Filters are never touched here.
    pop(e) {
      if (this.closing) {
        this.closing = false;
        if (!hashId()) return this.settle();
      }
      const id = hashId();
      if (id && this.cards.some((c) => c.id === id)) {
        this.pushed = !!(e.state && e.state.sisuInsp);
        this.open(id, false);
      } else if (this.lb.open) {
        this.pushed = false;
        this.lb.close();
      }
    }
  }
  customElements.define('sisu-inspiration', SisuInspiration);
})();
